import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, BookOpen, Globe2, Star, Flame } from "lucide-react";

const useCountUp = (target, duration = 1800) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const pad = (n) => String(n).padStart(2, "0");

const useMidnightCountdown = () => {
  const calc = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const diff = Math.max(0, midnight - now);
    return {
      h: Math.floor(diff / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [tl, setTl] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTl(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return tl;
};

const SocialProof = () => {
  const { t } = useTranslation();
  const students = useCountUp(1200);
  const coursesN = useCountUp(40);
  const countries = useCountUp(14);
  const { h, m, s } = useMidnightCountdown();

  const stats = [
    { icon: Users, value: `+${students.toLocaleString()}`, label: t("social.studentsLabel") },
    { icon: BookOpen, value: coursesN, label: t("social.coursesLabel") },
    { icon: Globe2, value: countries, label: t("social.countriesLabel") },
    { icon: Star, value: "4.9/5", label: t("social.ratingLabel") },
  ];

  const scrollToCourses = () =>
    document.getElementById("cursos")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="social-proof-section" className="bg-[#090D16] text-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {stats.map((st) => (
            <div key={st.label} data-testid={`proof-stat-${st.label}`}>
              <st.icon className="w-5 h-5 text-[#10B981] mb-3" strokeWidth={1.5} />
              <div className="font-display font-black text-4xl md:text-5xl tracking-tight">
                {st.value}
              </div>
              <div className="text-sm text-zinc-400 mt-1">{st.label}</div>
            </div>
          ))}
        </div>

        <div
          data-testid="daily-offer-banner"
          className="border border-slate-800 bg-[#111827] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
        >
          <div className="flex items-start gap-4 flex-1">
            <Flame className="w-8 h-8 text-[#FF3B30] flex-shrink-0" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF3B30] mb-1">
                {t("social.offerTitle")}
              </div>
              <p className="text-zinc-300 text-sm md:text-base">{t("social.offerDesc")}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                {t("social.offerEnds")}
              </div>
              <div
                data-testid="offer-countdown"
                className="font-mono font-bold text-2xl md:text-3xl tabular-nums"
              >
                {pad(h)}:{pad(m)}:{pad(s)}
              </div>
            </div>
            <button
              onClick={scrollToCourses}
              data-testid="offer-cta-btn"
              className="bg-[#FF3B30] hover:bg-[#E02E24] text-white px-6 py-3.5 font-semibold transition whitespace-nowrap"
            >
              {t("social.offerCta")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
